import { Form } from 'formik';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useLocation, useNavigate } from 'react-router-dom';
// @ts-ignore // eslint-disable-next-line
import useMobileDetect from 'use-mobile-detect-hook';

import { IconSend } from 'components/Icons';

import { useConversation } from 'hooks/conversation';
import { useAnalytics } from 'hooks/useAnalytics';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';
import { DataDogLogger } from 'utils/dataDog/logger';
import { scrollToMessagesListBottom } from 'utils/message';

import { ANALYTICS } from 'analytics/constants';
import {
  AnalyticFormErrorDetailsType,
  AnalyticFormSubmittedDetailsType,
  AnalyticReconnectedDetailsType,
} from 'analytics/types';
import FormWrapper from 'components/FormWrapper';
import { FORM_IDS } from 'components/FormWrapper/constants';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { ACTIONS } from 'constants/hook';
import { QUESTION_INPUT_FIELD_ID } from 'constants/id';
import { ROUTES } from 'routes/constants';
import { saveInputToStorage } from './helpers';
import {
  QuestionFieldWrapper,
  StyledBaseInputField,
  StyledIconButton,
  cssQuestionInputDefault,
} from './styles';

const QuestionField = ({ id }: { id: string }): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const detectMobile = useMobileDetect();
  const { trackEvent } = useAnalytics();
  const { showBoundary } = useErrorBoundary();
  const { conversationState, dispatchConversationAction } = useConversation();
  const { isConnected, isInitializationRequested, questionInputText, questionWhenInitializing } =
    conversationState;

  const [isNewMsgWaitingForTwilioReconnect, setIsNewMsgWaitingForTwilioReconnect] = useState(false);

  const questionInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sendDelayedMessage = async () => {
      if (isConnected && isNewMsgWaitingForTwilioReconnect) {
        try {
          await window[CHATUI_WINDOW_PATH].sendMessage(questionInputText);
          setIsNewMsgWaitingForTwilioReconnect(false);
        } catch (e: any) {
          return showBoundary(e);
        }
      }
    };
    sendDelayedMessage();
  }, [isConnected]);

  const handleOnChange = (newStrValue: string) => {
    // prevent unnecessary empty new line
    if (newStrValue === '\n') return '';

    // Persist user input across pages
    saveInputToStorage(newStrValue);

    // duplicate Formik value in conversationState so user input can persist on component unmount
    dispatchConversationAction({
      type: ACTIONS.CHANGE,
      payload: { questionInputText: newStrValue },
    });
    return newStrValue;
  };

  const handleOnSubmit = async (values: { questionInputText: string }) => {
    if (questionInputText.toLowerCase() === 'chat_ui_debug') {
      DataDogLogger.action(CUSTOM_ACTION_NAMES.CHAT_UI_DEBUG_PANEL_SHOWN);
      navigate(ROUTES.DEBUG);
      return;
    }

    window[CHATUI_WINDOW_PATH].startInactivityCounter();

    saveInputToStorage('');

    const msg = values.questionInputText;
    if (msg.trim() === '') {
      dispatchConversationAction({
        type: ACTIONS.CHANGE,
        payload: { questionInputText: '' },
      });
      return;
    }
    if (isConnected) {
      try {
        await window[CHATUI_WINDOW_PATH].sendMessage(msg);
        trackEvent({
          analyticEvent: ANALYTICS.EVENT.FORM_SUBMITTED,
          details: { analytic_form_id: FORM_IDS.QUESTION_FORM } as AnalyticFormSubmittedDetailsType,
        });
      } catch (e: any) {
        trackEvent({
          analyticEvent: ANALYTICS.EVENT.FORM_FAILED,
          details: {
            analytic_form_id: FORM_IDS.QUESTION_FORM,
            error_message: e,
          } as AnalyticFormErrorDetailsType,
        });
        return showBoundary(e);
      }
    } else {
      if (conversationState.isInactivityTimeout) {
        const context = { method: 'New message' };
        DataDogLogger.action(CUSTOM_ACTION_NAMES.INACTIVITY_RECONNECT, context);
        trackEvent({
          analyticEvent: ANALYTICS.EVENT.CHAT_RECONNECTED,
          details: { reconnect_method: context.method } as AnalyticReconnectedDetailsType,
        });
      }
      dispatchConversationAction({
        type: ACTIONS.CHANGE,
        payload: {
          isConnected: false,
          isInitializationRequested: true,
          questionInputText: '',
          questionWhenInitializing: msg,
        },
      });
    }
    setTimeout(() => {
      scrollToMessagesListBottom(); // let animations finish parsing first
    }, 1000);
    if (location.pathname === ROUTES.HOME) navigate(ROUTES.CHAT);
    questionInputRef.current?.setAttribute('style', cssQuestionInputDefault.toString());
    questionInputRef.current?.blur();
  };

  return (
    <QuestionFieldWrapper id={id}>
      <FormWrapper
        id={FORM_IDS.QUESTION_FORM}
        initialValues={{
          questionInputText: questionWhenInitializing ? '' : questionInputText,
        }}
        onSubmit={async (values, { resetForm }) => {
          await handleOnSubmit(values);
          resetForm({ values: { questionInputText: '' } });
        }}
      >
        {({ values, isSubmitting, setFieldValue, submitForm }) => {
          const isTwilioReconnecting =
            conversationState.twilio && isInitializationRequested && !isConnected;
          const isSubmitDisabled =
            values.questionInputText === '' || isTwilioReconnecting || isSubmitting;
          return (
            <Form noValidate>
              <div id='input-and-send-wrapper'>
                <StyledBaseInputField
                  onKeyDown={async (e: any) => {
                    const isValidSubmission =
                      !detectMobile.isMobile() &&
                      e.key === 'Enter' &&
                      !e.shiftKey &&
                      values.questionInputText.length;
                    isValidSubmission && (await submitForm());
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    !isSubmitting &&
                    setFieldValue('questionInputText', handleOnChange(e.target.value))
                  }
                  disabled={Boolean(isTwilioReconnecting)}
                  value={values.questionInputText}
                  placeholder='Ask Anything'
                  id={QUESTION_INPUT_FIELD_ID}
                  ref={questionInputRef}
                  name='questionField'
                  showClear={false}
                  showValid={false}
                  autoComplete='off'
                  type='text'
                />
                <StyledIconButton
                  type='submit'
                  disabled={isSubmitDisabled}
                  onSubmit={submitForm}
                  data-dd-action-name='Send Message Button'
                >
                  <IconSend />
                </StyledIconButton>
              </div>
            </Form>
          );
        }}
      </FormWrapper>
    </QuestionFieldWrapper>
  );
};

export default QuestionField;
